<template>
  <div class="utils-tab">
    <b-container fluid>
      <!-- Section: Utility Tools -->
      <b-row class="mb-4">
        <b-col cols="12">
          <div class="section-header">
            <h3 class="section-title">
              <i class="fas fa-tools mr-2" />
              {{ $t('admcfg.utilityTools', 'Utility Tools') }}
            </h3>
            <p class="section-description">
              {{ $t('admcfg.utilityDescription', 'Access utility functions for system maintenance and debugging') }}
            </p>
          </div>
        </b-col>
      </b-row>

      <!-- Utility Cards Grid -->
      <b-row class="mb-4">
        <b-col cols="12" md="6" lg="4" class="mb-3">
          <utility-card
            title="Cache Management"
            description="Clear and manage application cache"
            icon="fas fa-trash"
            variant="warning"
          />
        </b-col>
        <b-col cols="12" md="6" lg="4" class="mb-3">
          <utility-card
            title="Database Tools"
            description="Manage local database and storage"
            icon="fas fa-database"
            variant="info"
          />
        </b-col>
        <b-col cols="12" md="6" lg="4" class="mb-3">
          <utility-card
            title="Logs & Diagnostics"
            description="View system logs and diagnostics"
            icon="fas fa-bug"
            variant="danger"
          />
        </b-col>
        <b-col cols="12" md="6" lg="4" class="mb-3">
          <utility-card
            title="Users"
            description="Recover user passwords"
            icon="fas fa-users"
            variant="primary"
            @click="showUsersModal"
          />
        </b-col>
      </b-row>

      <!-- Section: System Information -->
      <b-row>
        <b-col cols="12">
          <div class="section-header">
            <h3 class="section-title">
              <i class="fas fa-info-circle mr-2" />
              {{ $t('admcfg.systemInfo', 'System Information') }}
            </h3>
          </div>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12" md="6">
          <div class="info-card">
            <h5 class="info-card-title">
              <i class="fas fa-cube mr-2" />
              {{ $t('admcfg.appVersion', 'Application Information') }}
            </h5>
            <div class="info-card-content">
              <div class="info-row">
                <span class="info-label">{{ $t('admcfg.version', 'Version:') }}</span>
                <span class="info-value">v2.0.0</span>
              </div>
              <div class="info-row">
                <span class="info-label">{{ $t('admcfg.environment', 'Environment:') }}</span>
                <span class="info-value">{{ nodeEnv }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">{{ $t('admcfg.vueVersion', 'Vue Version:') }}</span>
                <span class="info-value">2.7.16</span>
              </div>
            </div>
          </div>
        </b-col>

        <b-col cols="12" md="6">
          <div class="info-card">
            <h5 class="info-card-title">
              <i class="fas fa-user mr-2" />
              {{ $t('admcfg.adminInfo', 'Administrator Information') }}
            </h5>
            <div class="info-card-content">
              <div class="info-row">
                <span class="info-label">{{ $t('admcfg.email', 'Email:') }}</span>
                <span class="info-value">{{ userEmail }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">{{ $t('admcfg.role', 'Role:') }}</span>
                <span class="info-value">Administrator</span>
              </div>
              <div class="info-row">
                <span class="info-label">{{ $t('admcfg.loginTime', 'Last Login:') }}</span>
                <span class="info-value">{{ loginTime }}</span>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-container>

    <!-- Users Modal -->
    <users-modal ref="usersModal" />
  </div>
</template>

<script>
import UtilityCard from '../components/UtilityCard.vue'
import UsersModal from '../components/UsersModal.vue'

export default {
  name: 'UtilsTab',
  components: {
    UtilityCard,
    UsersModal,
  },
  data() {
    return {
      loginTime: new Date().toLocaleString(),
    }
  },
  computed: {
    userEmail() {
      return this.$store.state.auth.user?.email || 'Unknown'
    },
    nodeEnv() {
      return process.env.NODE_ENV || 'unknown'
    },
  },
  methods: {
    showUsersModal() {
      this.$bvModal.show('users-modal')
    },
  },
}
</script>

<style scoped>
.utils-tab {
  padding: 20px 0;
}

.section-header {
  padding-bottom: 15px;
  border-bottom: 2px solid #e9ecef;
  margin-bottom: 20px;
}

.section-title {
  color: #0066cc;
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 5px;
}

.section-description {
  color: #666;
  font-size: 0.9rem;
  margin: 0;
}

.info-card {
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 20px;
  margin-bottom: 20px;
}

.info-card-title {
  color: #0066cc;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e9ecef;
}

.info-card-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.95rem;
}

.info-label {
  color: #666;
  font-weight: 500;
}

.info-value {
  color: #0066cc;
  font-weight: 600;
}

.utility-card {
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  height: 100%;
}

.utility-card:hover {
  box-shadow: 0 4px 12px rgba(0, 102, 204, 0.15);
  transform: translateY(-2px);
}

.utility-card-header {
  font-size: 2rem;
  color: #0066cc;
  margin-bottom: 15px;
  min-width: 40px;
}

.utility-card-body {
  flex: 1;
}

.utility-card-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #0066cc;
  margin-bottom: 5px;
  margin: 0;
}

.utility-card-description {
  font-size: 0.9rem;
  color: #666;
  margin: 0;
}
</style>
